import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import axios from 'axios';
import { storage } from '../utils/storage';
import { emitLogout } from './authEvents';

/**
 * API Client using httpOnly cookies for authentication
 *
 * Authentication: Tokens stored in httpOnly cookies (managed by backend)
 * CSRF Protection: Double-submit cookie pattern for state-changing operations
 * Auto-Refresh: Automatically refreshes tokens on 401 errors
 *
 * Session strategy for day-long operations:
 * - Access token: 15 min, refresh token: 30 days
 * - Proactive refresh blocks outgoing requests when token is near-expiry
 * - On 401, a single refresh attempt is shared across all concurrent requests
 * - Circuit breaker prevents infinite refresh loops
 * - Only redirects to login when refresh token is truly exhausted
 *
 * Base URL:
 * - Development: Uses relative path (/api) - Vite proxy forwards to backend
 * - Production: Uses absolute URL from environment variable
 */
export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // CRITICAL: Required for cookie-based auth
});

// Token lifecycle tracking
const ACCESS_TOKEN_LIFETIME_MS = 15 * 60 * 1000; // 15 minutes
const PROACTIVE_REFRESH_THRESHOLD_MS = 2 * 60 * 1000; // Refresh 2 min before expiry
let tokenExpiresAt: number | null = null;
let isLoggingOut = false;

// Unified refresh state — shared between request and response interceptors
let refreshPromise: Promise<void> | null = null;

// Circuit breaker: prevent rapid-fire refresh attempts
const REFRESH_COOLDOWN_MS = 5_000; // 5 seconds between refresh attempts
let lastRefreshAttemptAt = 0;
let consecutiveRefreshFailures = 0;
const MAX_CONSECUTIVE_REFRESH_FAILURES = 3;

export function setTokenExpiry(): void {
  tokenExpiresAt = Date.now() + ACCESS_TOKEN_LIFETIME_MS;
  consecutiveRefreshFailures = 0;
}

export function clearTokenExpiry(): void {
  tokenExpiresAt = null;
}

/**
 * Signal that a logout is in progress to suppress proactive token refresh
 * and prevent re-authentication while logout completes.
 */
export function setLoggingOut(value: boolean): void {
  isLoggingOut = value;
  if (value) {
    tokenExpiresAt = null;
  }
}

/**
 * Perform a token refresh, reusing any in-flight refresh to avoid duplicates.
 * Returns a promise that resolves when the refresh completes (or rejects on failure).
 */
function doRefresh(): Promise<void> {
  if (refreshPromise) {
    return refreshPromise;
  }

  const now = Date.now();

  // Circuit breaker: too many consecutive failures → give up
  if (consecutiveRefreshFailures >= MAX_CONSECUTIVE_REFRESH_FAILURES) {
    return Promise.reject(new Error('Max refresh attempts exceeded'));
  }

  // Cooldown: don't hammer the refresh endpoint
  if (now - lastRefreshAttemptAt < REFRESH_COOLDOWN_MS) {
    if (refreshPromise) return refreshPromise;
    // A refresh succeeded recently and the cookie is fresh — let concurrent requests proceed
    if (consecutiveRefreshFailures === 0) return Promise.resolve();
    return Promise.reject(new Error('Refresh cooldown active'));
  }

  lastRefreshAttemptAt = now;

  refreshPromise = apiClient
    .post('/auth/refresh', {})
    .then(() => {
      tokenExpiresAt = Date.now() + ACCESS_TOKEN_LIFETIME_MS;
      consecutiveRefreshFailures = 0;
    })
    .catch((err) => {
      consecutiveRefreshFailures++;
      throw err;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

// Public routes that don't require authentication
const PUBLIC_ROUTES = [
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/venues',
  '/terms',
  '/privacy',
  '/refund-policy',
  '/contact',
  '/staff/invitation/accept',
];

/**
 * Check if current route is public (doesn't require auth)
 */
const isPublicRoute = (): boolean => {
  const pathname = window.location.pathname;

  if (PUBLIC_ROUTES.includes(pathname)) {
    return true;
  }

  // Check for venue detail and booking pages (dynamic routes)
  const venueRoutePattern = /^\/[^/]+\/[^/]+\/[^/]+(\/book)?$/;
  if (venueRoutePattern.test(pathname)) {
    return true;
  }

  return false;
};

/**
 * Check if user has auth cookies by verifying user data exists
 * (Since httpOnly cookies can't be read, we use localStorage user as proxy)
 */
const hasAuthData = (): boolean => {
  return !!storage.getUser();
};

/**
 * Check if the access token is expired or near-expiry.
 * Returns true if we should proactively refresh before sending the request.
 */
const shouldProactivelyRefresh = (): boolean => {
  if (isLoggingOut) return false;
  // After page refresh, tokenExpiresAt is null but user may still have valid session.
  // Trigger a proactive refresh so the first request gets a fresh access token.
  if (tokenExpiresAt === null) return hasAuthData();
  return Date.now() > tokenExpiresAt - PROACTIVE_REFRESH_THRESHOLD_MS;
};

/**
 * Redirect to login with session-expired flag.
 * Used only for the fast-path when no auth data exists (not logged in).
 * For session expiry after a failed refresh, use emitLogout() instead.
 */
let redirecting = false;
const redirectToLogin = (): void => {
  if (redirecting) return;
  if (window.location.pathname === '/login') return;

  redirecting = true;
  sessionStorage.setItem('lov_return_to', window.location.pathname + window.location.search);
  sessionStorage.setItem('lov_session_expired', 'true');
  window.location.href = '/login';
};

/**
 * Reset all module-level state. Only intended for use in tests.
 * @internal
 */
export function __resetClientStateForTests(): void {
  redirecting = false;
  refreshPromise = null;
  lastRefreshAttemptAt = 0;
  consecutiveRefreshFailures = 0;
  tokenExpiresAt = null;
  isLoggingOut = false;
}

// Proactively refresh when the user returns to the tab after a long absence.
// This prevents a cascade of simultaneous 401s when React Query queries fire.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    if (isLoggingOut || !hasAuthData()) return;
    if (tokenExpiresAt === null || Date.now() >= tokenExpiresAt) {
      doRefresh().catch(() => {
        // 401 response interceptor will handle the failure when queries fire
      });
    }
  });
}

// Request interceptor — proactive refresh BLOCKS the request until token is fresh
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Add CSRF token for state-changing operations
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(config.method?.toUpperCase() || '')) {
      const csrfToken = storage.getCsrfToken();
      if (csrfToken && config.headers) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    }

    // Proactive refresh: AWAIT refresh before sending the request
    // Skip for the refresh endpoint itself to prevent recursion
    if (!config.url?.includes('/auth/refresh') && shouldProactivelyRefresh()) {
      try {
        await doRefresh();
      } catch {
        // If proactive refresh fails, let the request go through —
        // the 401 response interceptor will handle it
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor — handle 401 with unified refresh
apiClient.interceptors.response.use(
  (response) => {
    // Auto-unwrap standard API response envelope
    if (response.data?.success === true && 'data' in response.data) {
      const meta = response.data.meta;
      const data = response.data.data;

      if (meta?.pagination) {
        response.data = {
          data,
          total: meta.pagination.total,
          page: meta.pagination.page,
          limit: meta.pagination.limit,
          totalPages: meta.pagination.totalPages,
        };
      } else {
        response.data = data;

        if (meta && response.data && typeof response.data === 'object') {
          Object.defineProperty(response.data, '_meta', {
            value: meta,
            enumerable: false,
            writable: false,
          });
        }
      }
    }

    // Handle CSRF token in unwrapped data (cross-subdomain login response)
    if (response.data?.csrfToken) {
      storage.setCsrfToken(response.data.csrfToken);
    }

    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Network error (no response) — don't treat as auth failure
    if (!error.response) {
      return Promise.reject(error);
    }

    // Only handle 401, and only retry once per request
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // A 401 on the login endpoint means wrong credentials — not a session expiry.
    // Skip token refresh to preserve the original AUTH_INVALID_CREDENTIALS error.
    if (originalRequest.url?.includes('/auth/login')) {
      return Promise.reject(error);
    }

    // A 401 on the refresh endpoint means the refresh token is expired or revoked.
    // Calling doRefresh() here would create a circular Promise dependency (deadlock):
    // refreshPromise is the chain this interceptor runs inside, so awaiting it hangs forever.
    // Emit logout directly so the SessionExpiredOverlay appears immediately.
    if (originalRequest.url?.includes('/auth/refresh')) {
      clearTokenExpiry();
      if (!isPublicRoute()) {
        emitLogout();
      }
      return Promise.reject(error);
    }

    // Skip refresh if logout is in progress
    if (isLoggingOut) {
      return Promise.reject(error);
    }

    // Skip refresh if no auth data exists (user not logged in)
    if (!hasAuthData()) {
      if (!isPublicRoute()) {
        redirectToLogin();
      }
      return Promise.reject(error);
    }

    // Mark this request so we don't retry it infinitely
    originalRequest._retry = true;

    try {
      // Use the shared doRefresh — multiple concurrent 401s share one refresh call
      await doRefresh();
      // Refresh succeeded — retry the original request with the new cookie
      return await apiClient(originalRequest);
    } catch {
      // Refresh definitively failed — notify AuthContext via event emitter.
      // AuthContext will call storage.clearAuth(), reset React state, and show the overlay.
      clearTokenExpiry();

      if (!isPublicRoute()) {
        emitLogout();
      }

      return Promise.reject(error);
    }
  }
);
